/** Lead calls. Shapes follow workers/api/src/leads/{routes,store,stage-routes}.ts on `main` (checked 2026-11-02). */
import type { ApiClient } from "./client";

export type StageType = "open" | "won" | "lost";
export interface Stage { id: string; name: string; type: StageType }

export interface LeadTouch { seq: number; source: string; at: string; externalId: string | null; campaign: string | null }
export interface Lead {
  id: string;
  /** `+91…` for a role with `see_phone`, else the API's mask (`******1234`). */
  phone: string;
  name: string | null;
  email: string | null;
  branchId: string | null;
  source: string;
  stageId: string;
  ownerId: string | null;
  createdAt: string;
  lastSeenAt: string;
  followUpDueAt: string | null;
  firstContactedAt: string | null;
  trialAt: string | null;
  trialDoneAt: string | null;
  trialNoShowAt: string | null;
  missedAt: string | null;
  previousLeadId: string | null;
  memberId: string | null;
  updatedAt: number;
  /** Only on `GET /leads/:id`. */
  touches?: LeadTouch[];
}

export interface LeadFilters { stage?: string; owner?: string; source?: string; branch?: string; q?: string }
export interface LeadPage { leads: Lead[]; nextCursor: string | null }

/** A to-do the rules engine computes for one lead (`GET /leads/tasks`; engagement `DueTask` plus the lead's name). */
export interface LeadTask {
  key: string;
  leadId: string;
  leadName: string | null;
  ruleId: string;
  kind: "call_new_lead" | "trial_reminder" | "post_trial_follow_up" | (string & {});
  effect: string;
  ownerId: string | null;
  dueAt: string;
  overdueMs: number;
}

export const PAGE_SIZE = 50;

export const listLeads = (c: ApiClient, f: LeadFilters, cursor?: string | null, limit = PAGE_SIZE, signal?: AbortSignal) =>
  c.get<LeadPage>("/leads", { ...f, limit, cursor: cursor ?? undefined }, signal);
export const getLead = (c: ApiClient, id: string) => c.get<{ lead: Lead }>(`/leads/${id}`).then((r) => r.lead);
export const listStages = (c: ApiClient) => c.get<{ stages: Stage[] }>("/lead-stages").then((r) => r.stages);
export const listLeadTasks = (c: ApiClient) => c.get<{ tasks: LeadTask[] }>("/leads/tasks").then((r) => r.tasks);
export const doneLeadTask = (c: ApiClient, leadId: string, key: string) => c.post<{ done: string[] }>(`/leads/${leadId}/tasks/${encodeURIComponent(key)}/done`);

export interface NewLead { phone: string; name?: string; email?: string; source?: string; branchId: string; ownerId?: string; campaign?: string }
export const createLead = (c: ApiClient, v: NewLead, idempotencyKey?: string) =>
  c.post<{ action: "created" | "merged" | "duplicate" | string; lead: Lead }>("/leads", v, idempotencyKey ? { idempotencyKey } : undefined);

export interface LeadPatch { name?: string | null; email?: string | null; branchId?: string | null; ownerId?: string | null; trialAt?: string | null; trialDoneAt?: string | null; trialNoShowAt?: string | null }
export const patchLead = (c: ApiClient, id: string, v: LeadPatch) => c.patch<{ lead: Lead }>(`/leads/${id}`, v).then((r) => r.lead);
export const moveLead = (c: ApiClient, id: string, stageId: string) => c.post<{ lead: Lead }>(`/leads/${id}/move`, { stageId }).then((r) => r.lead);
export const deleteLead = (c: ApiClient, id: string) => c.del<{ deleted: true }>(`/leads/${id}`);

export interface ConvertInput { name?: string; email?: string; homeBranchId?: string; ownerStaffId?: string }
export const convertLead = (c: ApiClient, id: string, v: ConvertInput) =>
  c.post<{ lead: Lead; memberId: string; created: boolean }>(`/leads/${id}/convert`, v);

/** `GET /staff` (needs `staff:view`): names for owner filters and assignment. */
export interface StaffBrief { id: string; name: string; homeBranchId: string | null; status: string }
export const listStaff = (c: ApiClient) => c.get<{ staff: StaffBrief[]; nextCursor: string | null }>("/staff", { status: "active", limit: 200 }).then((r) => r.staff);
